/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewVCDetails.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import PropTypes from "prop-types";
import SaveIcon from "@mui/icons-material/Save";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import CustomSelectV2 from "../common/select/CustomSelectV2";
import CustomSelectVariantColor from "../common/colorpicker/CustomSelectVariantColor";
import CloseIcon from "@mui/icons-material/Close";
function ViewVCDetails({ variantDetails, handleEditClick, variant }) {
  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState("");

  const handleStartEdit = (item) => {
    setEditingField(item.label);
    setEditedValue(item.value);
  };

  const handleSave = (label) => {
    if (handleEditClick) handleEditClick(label, editedValue, variant);
    setEditingField(null);
  };
  return (
    <div>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          rowGap: 1.5,
          columnGap: 4,
          mt: 2,
        }}
      >

        {variantDetails?.map((item, index) => (
          <Box
            key={item?.label || "vc-" + index}
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: "1 1 calc(50% - 16px)",
              minWidth: "45%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 500,
                  color: "text.secondary",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                {item?.label}
              </Typography>

              {editingField === item.label ? (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    disabled={
                      !editedValue ||
                      editedValue === item?.value
                    }
                    size="small"
                    onClick={() => handleSave(item.label)}
                    sx={{ color: "#000" }}
                  >
                    <SaveIcon sx={{ fontSize: 18 }} />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => setEditingField(null)}
                    sx={{ color: "#000" }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ) : (
                item?.editable && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      item?.openDialog
                        ? handleEditClick?.(item.label, item.value, variant)
                        : handleStartEdit(item)
                    }
                  >
                    <BorderColorIcon sx={{ color: "#000", fontSize: 16 }} />
                  </IconButton>
                )
              )}
            </Box>
            {editingField === item.label && (
              <Box sx={{ mt: 1 }}>
                {item?.label === "Vehicle Status" && (
                  <CustomSelectV2
                    showLabel={false}
                    required={false}
                    handleOnChange={(e) => setEditedValue(e.target.value)}
                    selectedValue={editedValue}
                    optionsList={item?.optionsList || []}
                  />
                )}
                {item?.label === "Variant Color" && (
                  <CustomSelectVariantColor
                    value={editedValue}
                    setValue={setEditedValue}
                  />
                )}
              </Box>
            )}
            {editingField !== item.label && (
              <Typography
                variant="subtitle2"
                textAlign={"start"}
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                {item?.value || "-"}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </div>
  );
}


ViewVCDetails.propTypes = {
  variantDetails: PropTypes.array,
  handleEditClick: PropTypes.func,
  variant: PropTypes.any
};

export default ViewVCDetails;
